"use client";

import type { ChatMessage } from "../types";

import { createContext, use } from "react";

import type { AIFeedbackType } from "@/types/enums";

export interface AIChatState {
  messages: ChatMessage[];
  streamingText: string;
  isStreaming: boolean;
  isStopping?: boolean;
  error: string | null;
  /** What the agent is looking up right now, shown in place of the thinking verb. */
  activityLabel?: string | null;
}

export interface AIChatActions {
  sendMessage: (text: string, files?: File[]) => void;
  cancel: () => void;
  reset?: () => void;
  handleFeedback?: (
    messageId: string | undefined,
    feedbackType: AIFeedbackType,
    index: number,
  ) => void;
}

export interface AIChatFeedbackState {
  isOpen: boolean;
  feedbackText: string;
}

export interface AIChatFeedbackActions {
  openFeedbackDialog: () => void;
  closeFeedbackDialog: () => void;
  setFeedbackText: (text: string) => void;
  submitFeedback: () => void;
}

export interface AIChatContextValue {
  state: AIChatState;
  actions: AIChatActions;
  /** Optional feedback dialog state — when provided, AIChat.FeedbackDialog manages itself */
  feedback?: {
    state: AIChatFeedbackState;
    actions: AIChatFeedbackActions;
  };
}

export const AIChatContext = createContext<AIChatContextValue | null>(null);

export function useAIChatContext(): AIChatContextValue {
  const ctx = use(AIChatContext);
  if (!ctx) {
    throw new Error("useAIChatContext must be used within an AIChat.Provider");
  }
  return ctx;
}
